import {
  Typography,
  Stack,
  Button,
  Collapse,
  SxProps,
  Theme,
  Box,
  Tooltip,
} from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { GREY, SALMON } from "@/config/mui/colorPalette";
import {
  EventType,
  OnlineStoreSource,
} from "@/hooks/onlineStore/useOnlineStoreMixpanelEvents";
import { formattedPrice, numberIsGreaterThanZero } from "@/utils";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";
import TextButton from "../common/buttons/textButton";
import { useAnalytics } from "../common/segment/AnalyticsContext";
import { PackageChip } from "../serviceFlow/payments/paymentDrawer/packageSuggestionCard";
import PackageItemsList from "../serviceFlow/payments/paymentDrawer/paymentDrawerPackageItemsList";

export const PackageSuggestionBookingCard = ({
  packageItem,
  customizationPrimaryColor,
  medspaSlug,
  onlineStoreTrackingSource,
  sx,
}: {
  packageItem: MedspaOnlineStoreData["packages"][number];
  customizationPrimaryColor?: string;
  medspaSlug: string;
  onlineStoreTrackingSource: OnlineStoreSource;
  sx?: SxProps<Theme>;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const analytics = useAnalytics();

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipOpen, tooltipSetOpen] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);

  const collapseRef = useRef<HTMLDivElement>(null);

  // Add effect to check content height
  useLayoutEffect(() => {
    if (collapseRef.current) {
      const contentHeight = collapseRef.current.scrollHeight;
      setShowExpandButton(contentHeight > 85); // 150px is the collapsedSize
    }
  }, [packageItem.packageItems]); // Re-check when items change

  const handleBuyPackage = () => {
    analytics.track(EventType.CLIENT_SUGGESTED_PACKAGE_CLICKED, {
      name: packageItem.packageName,
      source: onlineStoreTrackingSource,
    });
    window.open(
      `/online-store/${medspaSlug}/packages?packageId=${packageItem.id}&source=${onlineStoreTrackingSource}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  useLayoutEffect(() => {
    if (textRef.current) {
      const isOverflowing =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setShowTooltip(isOverflowing);
    }
  }, [packageItem.packageName]);

  const shouldShowSavings = numberIsGreaterThanZero(packageItem.packageSavings);

  const handleTooltipClose = () => {
    if (!showTooltip) return;
    tooltipSetOpen(false);
  };

  const handleTooltipOpen = () => {
    if (!showTooltip) return;
    tooltipSetOpen(true);
  };

  return (
    <Stack
      key={packageItem.id}
      spacing={1.5}
      sx={{
        maxWidth: "400px",
        width: "100%",
        backgroundColor: GREY[5],
        borderRadius: "16px",
        padding: 2,
        border: `1px solid ${GREY[15]}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "fit-content",
        ...sx,
      }}
    >
      <Stack sx={{ width: "260px" }}>
        <Tooltip
          title={packageItem.packageName}
          open={tooltipOpen}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
          arrow
          enterTouchDelay={1500}
          leaveTouchDelay={500}
          slotProps={{
            popper: {
              sx: {
                userSelect: "none",
              },
            },
          }}
        >
          <Typography
            ref={textRef}
            variant="subtitleDefault"
            aria-label={packageItem.packageName}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {packageItem.packageName}
          </Typography>
        </Tooltip>
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography variant="labelSmall">
            {formattedPrice(packageItem.packagePrice)}
          </Typography>
          {packageItem.packageSavings && shouldShowSavings && (
            <PackageChip
              savings={packageItem.packageSavings}
              sx={{
                backgroundColor: SALMON[40],
                "& .MuiChip-label": {
                  color: SALMON[100],
                },
              }}
            />
          )}
        </Stack>
        <Collapse in={isExpanded} collapsedSize="85px" ref={collapseRef}>
          <PackageItemsList
            items={packageItem.packageItems}
            customizationPrimaryColor={customizationPrimaryColor}
          />
        </Collapse>
        <TextButton
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ display: showExpandButton ? "block" : "none" }}
        >
          {isExpanded ? "Hide perks" : "Show remaining perks"}
        </TextButton>
        <Box
          sx={{
            height: showExpandButton ? "0px" : "5px",
          }}
        />
      </Stack>
      <Button
        variant="contained"
        color="primary"
        sx={{
          whiteSpace: "nowrap",
          height: "40px",
          px: 5,
          borderRadius: "12px",
        }}
        onClick={handleBuyPackage}
      >
        <Typography variant="buttonSmall">Buy this package now</Typography>
      </Button>
    </Stack>
  );
};
